import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useLocation } from "react-router-dom";

import { useAPI } from "./useAPI";

export const usePreferences = () => {
  const api = useAPI();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [visitDetails, setVisitDetails] = useState([]);
  const [physicianContext, setPhysicianContext] = useState(null);

  useEffect(() => {
    // Get physician context from router state if available
    if (location.state?.physicianId) {
      setPhysicianContext({
        id: location.state.physicianId,
        name: location.state.physicianName,
      });
    }
  }, [location]);

  const fetchDocumentTypes = async (templateId) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/templates/${templateId}/document-types`);
      setDocumentTypes(data || []);
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to load document types");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitTypes = async (documentType, templateId) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/templates/${templateId}/visit-types`, {
        params: {
          document_type: documentType,
        },
      });
      setVisitTypes(data || []);
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to load visit types");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitDetails = async (visitTypeId, templateId) => {
    try {
      // Get the visit type data which includes details
      const { data } = await api.get(`/templates/${templateId}/visit-types/${visitTypeId}`);

      // Extract details from the visit type data
      const details = data?.data?.details || {};

      // Transform details object into array format
      const detailsArray = Object.entries(details).map(([detailId, detail]) => ({
        detail_id: detailId,
        ...detail,
      }));

      // Update state
      setVisitDetails(detailsArray);
      return detailsArray;
    } catch (error) {
      console.error("Error fetching visit details:", error);
      throw error;
    }
  };

  const updatePreferences = async (entityType, entityId, settings, scope = "user") => {
    try {
      setLoading(true);
      const { data } = await api.put(`/preferences/${entityType}/${entityId}?scope=${scope}`, {
        settings,
      });
      toast.success("Preferences updated successfully");
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to update preferences");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTemplates = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/preferences/document-type-templates");
      return data || [];
    } catch (err) {
      setError(err);
      toast.error("Failed to load document templates");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createDocumentType = async (templateId, docType, docData, exampleDocTypeId = null) => {
    try {
      setLoading(true);
      const { data } = await api.post(
        `/templates/${templateId}/document-types/${docType}`,
        docData,
        exampleDocTypeId
          ? {
              params: {
                example_doc_type_id: exampleDocTypeId,
              },
            }
          : undefined
      );
      toast.success("Document type created successfully");
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to create document type");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/templates/categories");
      return data || [];
    } catch (err) {
      setError(err);
      toast.error("Failed to load categories");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createVisitType = async (
    templateId,
    visitType,
    visitData,
    documentTypeId,
    exampleVisitTypeId = null
  ) => {
    try {
      setLoading(true);
      const params = {
        document_type_id: documentTypeId,
      };
      if (exampleVisitTypeId) {
        params.example_visit_type_id = exampleVisitTypeId;
      }
      const { data } = await api.post(
        `/templates/${templateId}/visit-types/${visitType}`,
        visitData,
        { params }
      );
      toast.success("Visit type created successfully");
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to create visit type");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createVisitDetail = async (templateId, visitType, detailName) => {
    try {
      setLoading(true);
      const { data } = await api.post(
        `/templates/${templateId}/visit-types/${visitType}/details/${detailName}`
      );
      toast.success("Visit detail created successfully");
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to create visit detail");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateVisitDetail = async (templateId, visitType, detailName, detailData) => {
    try {
      setLoading(true);
      const { data } = await api.put(
        `/templates/${templateId}/visit-types/${visitType}/details/${detailName}`,
        detailData
      );
      toast.success("Visit detail updated successfully");
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to update visit detail");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateDocumentType = async (templateId, docType, docData) => {
    try {
      setLoading(true);
      const { data } = await api.put(`/templates/${templateId}/document-types/${docType}`, docData);
      toast.success("Document type updated successfully");
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to update document type");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentType = async (templateId, docType) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/templates/${templateId}/document-types/${docType}`);
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to load document type");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitType = async (templateId, visitType) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/templates/${templateId}/visit-types/${visitType}`);
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to load visit type");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitDetail = async (templateId, visitTypeId, detailId) => {
    try {
      const { data } = await api.get(
        `/templates/${templateId}/visit-types/${visitTypeId}/details/${detailId}`
      );
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to load visit detail");
      throw err;
    }
  };

  const updateVisitType = async (templateId, visitType, visitData) => {
    try {
      setLoading(true);
      const { data } = await api.put(
        `/templates/${templateId}/visit-types/${visitType}`,
        visitData
      );
      toast.success("Visit type updated successfully");
      return data;
    } catch (err) {
      setError(err);
      toast.error("Failed to update visit type");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleVisitDetailChange = async (detailId, changes, templateId, visitTypeId) => {
    try {
      const currentDetail = visitDetails.find((d) => d.detail_id === detailId);
      const detailData = {
        name: changes.name || currentDetail?.name || detailId,
        description: changes.description || currentDetail?.description || "",
        instructions: changes.instructions || currentDetail?.instructions || "",
        example: changes.example || currentDetail?.example || "",
        required: currentDetail?.required || [],
        promptTemplate: currentDetail?.promptTemplate || "",
      };
      await updateVisitDetail(templateId, visitTypeId, detailId, detailData);
      await fetchVisitDetails(visitTypeId, templateId);
      toast.success("Visit detail updated successfully");
    } catch (error) {
      console.error("Error handling visit detail change:", error);
      toast.error("Failed to update visit detail");
      throw error;
    }
  };

  const fetchReportStyle = async (templateId) => {
    try {
      const { data } = await api.get(`/templates/${templateId}/report-style`);
      if (!data) {
        return { source_template_id: null, report_style: null };
      }
      return data;
    } catch (error) {
      console.error("Error fetching report style:", error);
      throw error;
    }
  };

  const updateReportStyle = async (templateId, reportStyle) => {
    try {
      const { data } = await api.put(`/templates/${templateId}/report-style`, reportStyle);
      return data.report_style;
    } catch (error) {
      console.error("Error updating report style:", error);
      throw error;
    }
  };

  const fetchTemplate = async (templateId) => {
    try {
      const { data } = await api.get(`/templates/${templateId}`);
      return data;
    } catch (error) {
      console.error("Error fetching template:", error);
      throw error;
    }
  };

  return {
    loading,
    error,
    documentTypes,
    visitTypes,
    visitDetails,
    physicianContext,
    fetchDocumentTypes,
    fetchVisitTypes,
    fetchVisitDetails,
    updatePreferences,
    fetchDocumentTemplates,
    createDocumentType,
    updateDocumentType,
    fetchCategories,
    createVisitType,
    createVisitDetail,
    updateVisitDetail,
    fetchDocumentType,
    fetchVisitType,
    fetchVisitDetail,
    updateVisitType,
    handleVisitDetailChange,
    fetchReportStyle,
    updateReportStyle,
    fetchTemplate,
    setVisitDetails,
  };
};
